import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  ExternalInvestmentBody,
  InvestmentProject,
  useUploadInvestmentLogoMutation,
  useUploadInvestmentPitchDeckMutation,
} from "../../../entities/admin-investments";
import { FileUploadInfo } from "../../../shared/ui/file-upload-info";
import {
  FILE_SIZE_LIMIT_BYTES,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";

const ACCEPTED_LOGO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const PITCH_DECK_MAX_BYTES = 10 * 1024 * 1024;

interface InvestmentsExternalFormDialogProps {
  open: boolean;
  investment: InvestmentProject | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (body: ExternalInvestmentBody) => void;
}

export const InvestmentsExternalFormDialog = ({
  open,
  investment,
  isSubmitting,
  onClose,
  onSubmit,
}: InvestmentsExternalFormDialogProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [videoPitchUrl, setVideoPitchUrl] = useState("");
  const [externalSource, setExternalSource] = useState("");
  const [founderFullName, setFounderFullName] = useState("");
  const [founderEmail, setFounderEmail] = useState("");
  const [founderPhone, setFounderPhone] = useState("");
  const [founderTelegram, setFounderTelegram] = useState("");
  const [founderWhatsapp, setFounderWhatsapp] = useState("");
  const [logoFileId, setLogoFileId] = useState<string | undefined>();
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>();
  const [pitchDeckFileId, setPitchDeckFileId] = useState<string | undefined>();
  const [pitchDeckFileName, setPitchDeckFileName] = useState<string | undefined>();

  const logoInputRef = useRef<HTMLInputElement>(null);
  const pitchDeckInputRef = useRef<HTMLInputElement>(null);
  const [uploadLogo, { isLoading: isUploadingLogo }] = useUploadInvestmentLogoMutation();
  const [uploadPitchDeck, { isLoading: isUploadingPitchDeck }] =
    useUploadInvestmentPitchDeckMutation();

  useEffect(() => {
    if (!open) return;

    if (investment) {
      setTitle(investment.title ?? "");
      setDescription(investment.description ?? "");
      setWebsite(investment.website ?? "");
      setVideoPitchUrl(investment.videoPitchUrl ?? "");
      setExternalSource(investment.externalSource ?? "");
      setFounderFullName(investment.founderFullName ?? "");
      setFounderEmail(investment.founderEmail ?? "");
      setFounderPhone(investment.founderPhone ?? "");
      setFounderTelegram(investment.founderTelegram ?? "");
      setFounderWhatsapp(investment.founderWhatsapp ?? "");
      setLogoFileId(investment.logoFileId ?? undefined);
      setLogoPreviewUrl(investment.logoUrl ?? undefined);
      setPitchDeckFileId(investment.pitchDeckFileId ?? undefined);
      setPitchDeckFileName(investment.pitchDeckUrl ? "Загруженный Pitch Deck" : undefined);
    } else {
      setTitle("");
      setDescription("");
      setWebsite("");
      setVideoPitchUrl("");
      setExternalSource("");
      setFounderFullName("");
      setFounderEmail("");
      setFounderPhone("");
      setFounderTelegram("");
      setFounderWhatsapp("");
      setLogoFileId(undefined);
      setLogoPreviewUrl(undefined);
      setPitchDeckFileId(undefined);
      setPitchDeckFileName(undefined);
    }

    if (logoInputRef.current) logoInputRef.current.value = "";
    if (pitchDeckInputRef.current) pitchDeckInputRef.current.value = "";
  }, [open, investment]);

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      alert("Допустимы JPEG, PNG и WebP");
      return;
    }
    const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
    if (sizeError) {
      alert(sizeError);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    try {
      const result = await uploadLogo(formData).unwrap();
      setLogoFileId(result.fileId);
      setLogoPreviewUrl(result.url || URL.createObjectURL(file));
    } catch {
      alert("Не удалось загрузить логотип");
    }
  };

  const handlePitchDeckChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Pitch Deck должен быть в формате PDF");
      return;
    }
    const sizeError = getFileSizeLimitError(file, PITCH_DECK_MAX_BYTES);
    if (sizeError) {
      alert(sizeError);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    try {
      const result = await uploadPitchDeck(formData).unwrap();
      setPitchDeckFileId(result.fileId);
      setPitchDeckFileName(file.name);
    } catch {
      alert("Не удалось загрузить Pitch Deck");
    }
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      alert("Укажите название проекта");
      return;
    }
    if (!description.trim()) {
      alert("Укажите описание проекта");
      return;
    }
    if (!logoFileId && !investment?.logoUrl) {
      alert("Загрузите логотип проекта");
      return;
    }

    const body: ExternalInvestmentBody = {
      title: title.trim(),
      description: description.trim(),
      website: website.trim() || undefined,
      videoPitchUrl: videoPitchUrl.trim() || undefined,
      externalSource: externalSource.trim() || undefined,
      founderFullName: founderFullName.trim() || undefined,
      founderEmail: founderEmail.trim() || undefined,
      founderPhone: founderPhone.trim() || undefined,
      founderTelegram: founderTelegram.trim() || undefined,
      founderWhatsapp: founderWhatsapp.trim() || undefined,
    };

    if (logoFileId) body.logoFileId = logoFileId;
    if (pitchDeckFileId) body.pitchDeckFileId = pitchDeckFileId;

    onSubmit(body);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {investment ? "Редактировать внешний проект" : "Создать внешний проект"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Название *"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            label="Описание *"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Сайт"
              fullWidth
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
            <TextField
              label="Источник"
              fullWidth
              value={externalSource}
              onChange={(e) => setExternalSource(e.target.value)}
              placeholder="Например, Startup.kz"
            />
          </Stack>
          <TextField
            label="Ссылка на Video Pitch Deck"
            fullWidth
            value={videoPitchUrl}
            onChange={(e) => setVideoPitchUrl(e.target.value)}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="ФИО основателя"
              fullWidth
              value={founderFullName}
              onChange={(e) => setFounderFullName(e.target.value)}
            />
            <TextField
              label="Email"
              fullWidth
              value={founderEmail}
              onChange={(e) => setFounderEmail(e.target.value)}
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Телефон"
              fullWidth
              value={founderPhone}
              onChange={(e) => setFounderPhone(e.target.value)}
            />
            <TextField
              label="Telegram"
              fullWidth
              value={founderTelegram}
              onChange={(e) => setFounderTelegram(e.target.value)}
            />
            <TextField
              label="WhatsApp"
              fullWidth
              value={founderWhatsapp}
              onChange={(e) => setFounderWhatsapp(e.target.value)}
            />
          </Stack>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Логотип *
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => logoInputRef.current?.click()}
                disabled={isUploadingLogo || isSubmitting}
              >
                {isUploadingLogo ? "Загрузка..." : "Загрузить логотип"}
              </Button>
              <FileUploadInfo type="avatar" recommendedSize="512×512 px" />
              {logoPreviewUrl && (
                <Box
                  component="img"
                  src={logoPreviewUrl}
                  alt="logo"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              )}
            </Stack>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handleLogoChange}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Pitch Deck (PDF)
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => pitchDeckInputRef.current?.click()}
                disabled={isUploadingPitchDeck || isSubmitting}
              >
                {isUploadingPitchDeck ? "Загрузка..." : "Загрузить PDF"}
              </Button>
              <FileUploadInfo type="any" maxSize="10 МБ" recommendedSize="PDF до 10 МБ" />
              {pitchDeckFileName && (
                <Typography variant="body2" color="text.secondary">
                  {pitchDeckFileName}
                </Typography>
              )}
            </Stack>
            <input
              ref={pitchDeckInputRef}
              type="file"
              accept="application/pdf"
              hidden
              onChange={handlePitchDeckChange}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || isUploadingLogo || isUploadingPitchDeck}
        >
          {investment ? "Сохранить" : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
